import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import { 
  ArrowLeft, Zap, Trophy, Award, Flame, Calendar,
  User, Mail, Shield, CreditCard, ChevronRight, Bitcoin, GraduationCap, Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [rewardStats, setRewardStats] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const rewardsRes = await api.get("/rewards/stats");
        setRewardStats(rewardsRes.data);
        await refreshUser();
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    };
    fetchData();
  }, [refreshUser]);

  const brainScore = typeof rewardStats?.bitcoin_brain_score === 'object'
    ? rewardStats.bitcoin_brain_score.score
    : (rewardStats?.bitcoin_brain_score || 0);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/users/account");
      logout();
      navigate("/");
    } catch {
      setDeleting(false);
    }
  };

  const completedSections = user?.sections_completed?.length || 0;
  const courseProgress = (completedSections / 21) * 100;

  const allBadges = [
    { id: "first_section", name: "First Steps", desc: "Complete your first section" },
    { id: "module_1_complete", name: "Module 1 Master", desc: "Complete Module 1" },
    { id: "halfway_there", name: "Halfway There", desc: "Complete 10 sections" },
    { id: "course_complete", name: "Bitcoin Brain", desc: "Complete all 21 sections" },
    { id: "quiz_master", name: "Quiz Master", desc: "Score 100% on 5 quizzes" },
    { id: "xp_1k", name: "First Thousand BTC XP", desc: "Earn 1,000 lifetime BTC XP" },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Dashboard</span>
            </button>
            <span className="font-heading text-lg font-bold text-white">
              <span className="text-[#F7931A]">PROFILE</span>
            </span>
            <div />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row items-center gap-6 mb-8">
          <div className="relative">
            <img 
              src={MASCOT_IMAGE} 
              alt="Profile" 
              className="w-24 h-24 rounded-2xl btc-glow"
            />
            <div className="absolute -bottom-2 -right-2 bg-[#F7931A] text-black px-2 py-0.5 rounded text-xs font-bold">
              Lv.{rewardStats?.xp_level || 1}
            </div>
          </div>
          <div className="text-center sm:text-left">
            <h1 className="font-heading text-3xl font-bold text-white">
              {user?.first_name}
            </h1>
            <p className="text-[#F7931A] font-medium">{rewardStats?.xp_title || "Fiat Sleeper"}</p>
            <div className="flex items-center gap-4 mt-2 text-zinc-500 text-sm">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                Joined {new Date(user?.join_date).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <Flame size={14} className="text-orange-400" />
                {rewardStats?.streak_days || 0} day streak
              </span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 text-center">
            <Zap className="text-[#F7931A] mx-auto mb-2" size={24} />
            <p className="font-mono text-2xl text-white">{rewardStats?.total_lifetime_xp || user?.total_lifetime_xp || 0}</p>
            <p className="text-zinc-500 text-sm">Lifetime BTC XP</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 text-center">
            <Trophy className="text-[#F7931A] mx-auto mb-2" size={24} />
            <p className="font-mono text-2xl text-white">{brainScore}</p>
            <p className="text-zinc-500 text-sm">Brain Score</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 text-center">
            <Award className="text-[#F7931A] mx-auto mb-2" size={24} />
            <p className="font-mono text-2xl text-white">{user?.badges?.length || 0}</p>
            <p className="text-zinc-500 text-sm">Badges</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 text-center">
            <Shield className="text-[#F7931A] mx-auto mb-2" size={24} />
            <p className="font-mono text-2xl text-white capitalize">{user?.purchase_tier || "Free"}</p>
            <p className="text-zinc-500 text-sm">Plan</p>
          </div>
        </div>

        {/* Course Progress */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-white">Course Progress</h2>
            <span className="text-[#F7931A] font-mono">{completedSections}/21</span>
          </div>
          <Progress value={courseProgress} className="h-3 mb-4" />
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 21 }, (_, i) => i + 1).map((section) => (
              <div
                key={section}
                className={`w-8 h-8 rounded flex items-center justify-center text-xs font-mono ${
                  user?.sections_completed?.includes(section)
                    ? "bg-[#F7931A] text-black"
                    : "bg-zinc-800 text-zinc-600"
                }`}
              >
                {section}
              </div>
            ))}
          </div>
        </div>

        {/* Badges */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mb-8">
          <h2 className="font-heading text-xl font-bold text-white mb-4">Achievements</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {allBadges.map((badge) => {
              const earned = user?.badges?.includes(badge.id);
              return (
                <div
                  key={badge.id}
                  className={`p-4 rounded-lg border ${
                    earned 
                      ? "bg-[#F7931A]/10 border-[#F7931A]/30" 
                      : "bg-zinc-900 border-zinc-800 opacity-50"
                  }`}
                >
                  <Trophy className={`mb-2 ${earned ? "text-[#F7931A]" : "text-zinc-600"}`} size={24} />
                  <p className={`font-medium ${earned ? "text-white" : "text-zinc-600"}`}>
                    {badge.name}
                  </p>
                  <p className="text-zinc-500 text-xs">{badge.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Easter Eggs */}
        {user?.easter_eggs?.length > 0 && (
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mb-8">
            <h2 className="font-heading text-xl font-bold text-white mb-4">
              Hidden Fragments Collected
            </h2>
            <div className="flex flex-wrap gap-2">
              {user.easter_eggs.map((egg) => (
                <span key={egg} className="badge-orange">
                  {egg.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Account Info */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mb-8">
          <h2 className="font-heading text-xl font-bold text-white mb-4">Account</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-white/5">
              <div className="flex items-center gap-3">
                <Mail className="text-zinc-500" size={18} />
                <div>
                  <p className="text-zinc-500 text-sm">Email</p>
                  <p className="text-white">{user?.email}</p>
                </div>
              </div>
              <span className={`text-xs ${user?.email_verified ? "text-green-400" : "text-yellow-400"}`}>
                {user?.email_verified ? "Verified" : "Unverified"}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-white/5">
              <div className="flex items-center gap-3">
                <CreditCard className="text-zinc-500" size={18} />
                <div>
                  <p className="text-zinc-500 text-sm">Subscription</p>
                  <p className="text-white capitalize">{user?.purchase_tier}</p>
                </div>
              </div>
              <Button
                onClick={() => navigate("/pricing")}
                variant="ghost"
                size="sm"
                className="text-[#F7931A]"
              >
                {user?.purchase_tier === "free" ? "Get Bitcoin Brain" : "Owned"}
              </Button>
            </div>
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Zap className="text-zinc-500" size={18} />
                <div>
                  <p className="text-zinc-500 text-sm">BTC Price on Join</p>
                  <p className="text-white font-mono">
                    ${user?.btc_price_on_join?.toLocaleString() || "—"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Educator Dashboard Link */}
        {user?.purchase_tier === "educator" && (
          <div className="bg-[#0A0A0A] border border-[#F7931A]/30 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GraduationCap className="text-[#F7931A]" size={24} />
                <div>
                  <h2 className="font-heading text-lg font-bold text-white">Educator Dashboard</h2>
                  <p className="text-zinc-500 text-sm">Manage up to 4 linked student accounts</p>
                </div>
              </div>
              <Button onClick={() => navigate("/educator")} className="btn-primary" data-testid="educator-dashboard-link">
                Open <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Delete Account */}
        <div className="bg-[#0A0A0A] border border-red-900/30 rounded-xl p-6 mb-8" data-testid="delete-account-section">
          <h2 className="font-heading text-lg font-bold text-red-400 mb-2">Danger Zone</h2>
          <p className="text-zinc-500 text-sm mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          {!showDeleteConfirm ? (
            <Button
              variant="outline"
              className="border-red-900/50 text-red-400 hover:bg-red-900/20 hover:text-red-300"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="delete-account-btn"
            >
              <Trash2 size={16} className="mr-2" /> Delete Account
            </Button>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteAccount}
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? "Deleting..." : "Yes, delete my account"}
              </Button>
              <Button
                variant="ghost"
                className="text-zinc-400"
                onClick={() => setShowDeleteConfirm(false)}
                data-testid="cancel-delete-btn"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
